/**
 * Verification Benefits Component
 * Displays current and potential benefits of verification
 */

import React from 'react';
import {
    CheckBadgeIcon,
    CurrencyDollarIcon,
    StarIcon,
    ChatBubbleLeftEllipsisIcon,
    MegaphoneIcon,
    LockClosedIcon,
    CheckCircleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import { VerificationBenefits } from '../../types/verification';
import { VERIFIED_USER_BENEFITS } from '../../constants/verification';
import { Accordion, AccordionItem } from '../ui/Accordion';

interface VerificationBenefitsProps {
    benefits: VerificationBenefits;
    isVerified: boolean;
    trustScore: number;
    showUpgradePrompt?: boolean;
    className?: string;
}

const VerificationBenefitsComponent: React.FC<VerificationBenefitsProps> = ({
    benefits,
    isVerified,
    trustScore,
    showUpgradePrompt = true,
    className = ''
}) => {
    // Map benefit icons
    const getBenefitIcon = (iconName: string) => {
        const iconClass = "w-6 h-6";

        switch (iconName) {
            case 'check-badge':
                return <CheckBadgeIcon className={iconClass} />;
            case 'currency-dollar':
                return <CurrencyDollarIcon className={iconClass} />;
            case 'infinity':
                return <ArrowPathIcon className={iconClass} />;
            case 'star':
                return <StarIcon className={iconClass} />;
            case 'chat-bubble-left-ellipsis':
                return <ChatBubbleLeftEllipsisIcon className={iconClass} />;
            case 'megaphone':
                return <MegaphoneIcon className={iconClass} />;
            default:
                return <CheckCircleIcon className={iconClass} />;
        }
    };

    // Check if benefit is active
    const isBenefitActive = (benefitKey: string): boolean => {
        switch (benefitKey) {
            case 'check-badge':
                return benefits.hasVerifiedBadge;
            case 'currency-dollar':
                return benefits.feeReduction > 0;
            case 'infinity':
                return benefits.canPostUnlimitedGigs;
            case 'star':
                return benefits.accessToPremiumCategories;
            case 'chat-bubble-left-ellipsis':
                return benefits.hasPrioritySupport;
            case 'megaphone':
                return benefits.isFeatured;
            default:
                return false;
        }
    };

    return (
        <div className={`space-y-6 ${className}`}>
            {/* Current Benefits */}
            <Accordion>
                <AccordionItem 
                    title={isVerified ? 'Your Active Benefits' : 'Your Current Access'}
                    defaultOpen={true}
                    icon={<CheckBadgeIcon className="w-5 h-5" />}
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {VERIFIED_USER_BENEFITS.map((benefit, index) => {
                        const isActive = isBenefitActive(benefit.icon);

                        return (
                            <div
                                key={index}
                                className={`
                                    relative p-4 border rounded-lg transition-all
                                    ${isActive
                                        ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                                        : 'card'
                                    }
                                `}
                            >
                                {/* Status indicator */}
                                <div className="absolute top-3 right-3">
                                    {isActive ? (
                                        <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                                    ) : (
                                        <LockClosedIcon className="w-5 h-5 text-neutral-400" />
                                    )}
                                </div>

                                {/* Benefit icon and content */}
                                <div className="flex items-start gap-3 pr-8">
                                    <div className={`
                                        p-2 rounded-lg
                                        ${isActive
                                            ? 'bg-green-100 dark:bg-green-800/30 text-green-600 dark:text-green-400'
                                            : 'bg-neutral-100 dark:bg-gray-700/50 text-neutral-500 dark:text-neutral-300'
                                        }
                                    `}>
                                        {getBenefitIcon(benefit.icon)}
                                    </div>

                                    <div>
                                        <h5 className={`
                                            font-medium mb-1
                                            ${isActive
                                                ? 'text-neutral-900 dark:text-neutral-100'
                                                : 'text-neutral-600 dark:text-neutral-400'
                                            }
                                        `}>
                                            {benefit.title}
                                            {benefit.icon === 'currency-dollar' && benefits.feeReduction > 0 && (
                                                <span className="ml-1 text-green-600 font-semibold">
                                                    ({benefits.feeReduction}% off)
                                                </span>
                                            )}
                                        </h5>

                                        <p className={`
                                            text-sm
                                            ${isActive
                                                ? 'text-neutral-700 dark:text-neutral-300'
                                                : 'text-neutral-500 dark:text-neutral-500'
                                            }
                                        `}>
                                            {benefit.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    </div>
                </AccordionItem>
            </Accordion>

            {/* Current Limits */}
            <Accordion>
                <AccordionItem 
                    title="Current Access Limits"
                    icon={<CurrencyDollarIcon className="w-5 h-5" />}
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="card p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                                <CurrencyDollarIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <span className="font-medium text-neutral-900 dark:text-neutral-100">
                                Gig Posting
                            </span>
                        </div>

                        <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                            {benefits.canPostUnlimitedGigs ? '∞' : `$${(trustScore * 10).toLocaleString()}`}
                        </div>

                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                            Maximum gig value
                        </div>
                    </div>

                    <div className="card p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-secondary-100 dark:bg-secondary-900/20 rounded-lg">
                                <StarIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
                            </div>
                            <span className="font-medium text-neutral-900 dark:text-neutral-100">
                                Applications
                            </span>
                        </div>

                        <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                            {benefits.canApplyUnlimitedGigs ? '∞' : `$${(trustScore * 15).toLocaleString()}`}
                        </div>

                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                            Maximum application value
                        </div>
                    </div>
                    </div>
                </AccordionItem>
            </Accordion>

            {/* Upgrade Prompt */}
            {!isVerified && showUpgradePrompt && (
                <div className="card p-6 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/10 dark:to-secondary-900/10 border border-primary-200 dark:border-primary-800">
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-xl">
                            <CheckBadgeIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                        </div>

                        <div className="flex-1">
                            <h4 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                                Unlock Premium Benefits
                            </h4>

                            <p className="text-neutral-700 dark:text-neutral-300 mb-4">
                                Complete your verification to unlock all premium benefits including unlimited gig values,
                                50% fee reduction, and priority support.
                            </p>

                            <div className="flex flex-wrap gap-2 mb-4">
                                {!benefits.hasVerifiedBadge && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-neutral-800 border border-primary-200 dark:border-primary-700 rounded-full text-sm font-medium text-primary-700 dark:text-primary-300">
                                        <CheckBadgeIcon className="w-4 h-4" />
                                        Verified Badge
                                    </span>
                                )}

                                {benefits.feeReduction === 0 && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-neutral-800 border border-green-200 dark:border-green-700 rounded-full text-sm font-medium text-green-700 dark:text-green-300">
                                        <CurrencyDollarIcon className="w-4 h-4" />
                                        50% Fee Reduction
                                    </span>
                                )}

                                {!benefits.canPostUnlimitedGigs && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-neutral-800 border border-secondary-200 dark:border-secondary-700 rounded-full text-sm font-medium text-secondary-700 dark:text-secondary-300">
                                        <ArrowPathIcon className="w-4 h-4" />
                                        Unlimited Gigs
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-3">
                                <button className="btn-primary">
                                    Start Verification
                                </button>

                                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                    Takes 5-10 minutes
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Trust Score Impact */}
            <Accordion>
                <AccordionItem 
                    title="How to Improve Your Benefits"
                    icon={<StarIcon className="w-5 h-5" />}
                >
                    <div className="space-y-3">
                    <div className="card flex items-start gap-3 p-3">
                        <div className="p-1.5 bg-primary-100 dark:bg-primary-900/20 rounded">
                            <CheckBadgeIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                            <div className="font-medium text-neutral-900 dark:text-neutral-100 mb-1">
                                Complete Verification (+30 points)
                            </div>
                            <div className="text-sm text-neutral-600 dark:text-neutral-400">
                                Upload required documents to get verified and unlock all premium benefits
                            </div>
                        </div>
                    </div>

                    <div className="card flex items-start gap-3 p-3">
                        <div className="p-1.5 bg-green-100 dark:bg-green-900/20 rounded">
                            <StarIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                            <div className="font-medium text-neutral-900 dark:text-neutral-100 mb-1">
                                Complete Gigs Successfully (+5 points each)
                            </div>
                            <div className="text-sm text-neutral-600 dark:text-neutral-400">
                                Finish gigs on time and maintain good communication with clients
                            </div>
                        </div>
                    </div>

                    <div className="card flex items-start gap-3 p-3">
                        <div className="p-1.5 bg-yellow-100 dark:bg-yellow-900/20 rounded">
                            <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div>
                            <div className="font-medium text-neutral-900 dark:text-neutral-100 mb-1">
                                Receive Positive Reviews (+10 points each)
                            </div>
                            <div className="text-sm text-neutral-600 dark:text-neutral-400">
                                Deliver quality work to earn 4-5 star reviews from satisfied clients
                            </div>
                        </div>
                    </div>
                    </div>
                </AccordionItem>
            </Accordion>
        </div>
    );
};

export default VerificationBenefitsComponent;